import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, Image, Search, Printer, Mail, Plus,
  DollarSign, MapPin, Bed, Bath, Square
} from 'lucide-react';
import { AppNavbar } from '../components/AppNavbar';

export const PrintCenterPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('flyer');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Property Data Form
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [propertyData, setPropertyData] = useState({
    price: '',
    address: '',
    beds: '',
    baths: '',
    sqft: '',
    description: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, selectedCategory, searchQuery]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/templates');
      // Filter only print templates (flyer, postcard, brochure)
      const printTemplates = response.data.templates.filter(
        t => t.type === 'flyer' || t.type === 'postcard' || t.type === 'brochure'
      );
      setTemplates(printTemplates);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.type === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredTemplates(filtered);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowPropertyForm(true);
  };

  const handleCreatePrint = () => {
    if (!selectedTemplate) return;
    
    // Navigate to template editor with property data
    navigate(`/create-campaign/${selectedTemplate.template_id}`, {
      state: { 
        propertyData,
        isPrint: true
      }
    });
  };


  const getCategoryIcon = (type) => {
    switch(type) {
      case 'flyer': return <FileText className="h-5 w-5" />;
      case 'postcard': return <Mail className="h-5 w-5" />;
      case 'brochure': return <FileText className="h-5 w-5" />;
      default: return <Printer className="h-5 w-5" />;
    }
  };

  const getCategoryLabel = (type) => {
    switch(type) {
      case 'flyer': return 'Flyer (8.5×11")';
      case 'postcard': return 'Postcard (4×6") - 2 Sides';
      case 'brochure': return 'Brochure (11×8.5") - 2 Sides';
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      <AppNavbar />

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Printer className="inline h-8 w-8 mr-3 text-primary" />
            Print Center
          </h2>
          <p className="text-muted-foreground">
            Create professional print materials for your listings. Auto-fill property details and download print-ready files.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search print templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-print-input"
            />
          </div>

          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="flyer" data-testid="filter-flyers">
                <FileText className="h-4 w-4 mr-2" />
                Flyers (8.5×11")
              </TabsTrigger>
              <TabsTrigger value="postcard" data-testid="filter-postcards">
                <Mail className="h-4 w-4 mr-2" />
                Postcards (4×6")
              </TabsTrigger>
              <TabsTrigger value="brochure" data-testid="filter-brochures">
                <FileText className="h-4 w-4 mr-2" />
                Brochures (11×8.5")
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p>Loading print templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-20">
            <Printer className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No print templates found.</p>
            {user?.role === 'admin' && (
              <Button onClick={() => navigate('/canvas-designer')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Print Template
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card 
                key={template.template_id} 
                className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg group"
                onClick={() => handleTemplateSelect(template)}
                data-testid={`print-template-${template.template_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(template.type)}
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {getCategoryLabel(template.type)}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground capitalize">
                      {template.category}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="aspect-[8.5/11] bg-muted/30 rounded-lg mb-4 flex items-center justify-center border border-border/50 group-hover:border-primary/30 transition-colors overflow-hidden">
                    {template.thumbnail_url ? (
                      <img 
                        src={template.thumbnail_url} 
                        alt={template.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-4">
                        {getCategoryIcon(template.type)}
                        <p className="text-xs text-muted-foreground mt-2">Preview</p>
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg mb-1">{template.name}</CardTitle>
                  <CardDescription>
                    Click to customize with your property details
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Property Data Dialog */}
        <Dialog open={showPropertyForm} onOpenChange={setShowPropertyForm}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5 text-primary" />
                Enter Property Details
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Fill in the property details below. These will auto-populate into your {selectedTemplate?.type} template.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="price" className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Price
                  </Label>
                  <Input
                    id="price"
                    placeholder="$499,000"
                    value={propertyData.price}
                    onChange={(e) => setPropertyData({...propertyData, price: e.target.value})}
                    data-testid="property-price-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="address" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Address
                  </Label>
                  <Input
                    id="address"
                    placeholder="123 Main St, City, State 12345"
                    value={propertyData.address}
                    onChange={(e) => setPropertyData({...propertyData, address: e.target.value})}
                    data-testid="property-address-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="beds" className="flex items-center gap-2">
                    <Bed className="h-4 w-4" />
                    Bedrooms
                  </Label>
                  <Input
                    id="beds"
                    type="number"
                    placeholder="4"
                    value={propertyData.beds}
                    onChange={(e) => setPropertyData({...propertyData, beds: e.target.value})}
                    data-testid="property-beds-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="baths" className="flex items-center gap-2">
                    <Bath className="h-4 w-4" />
                    Bathrooms
                  </Label>
                  <Input
                    id="baths"
                    type="number"
                    placeholder="3"
                    value={propertyData.baths}
                    onChange={(e) => setPropertyData({...propertyData, baths: e.target.value})}
                    data-testid="property-baths-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="sqft" className="flex items-center gap-2">
                    <Square className="h-4 w-4" />
                    Square Footage
                  </Label>
                  <Input
                    id="sqft"
                    placeholder="2,500"
                    value={propertyData.sqft}
                    onChange={(e) => setPropertyData({...propertyData, sqft: e.target.value})}
                    data-testid="property-sqft-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="Beautiful home with modern amenities..."
                    value={propertyData.description}
                    onChange={(e) => setPropertyData({...propertyData, description: e.target.value})}
                    data-testid="property-description-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowPropertyForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleCreatePrint}
                  data-testid="create-print-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Create Print
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default PrintCenterPage;
